import React, { useState, useEffect, useRef, useCallback } from "react";
import { BrowserRouter as Router, Routes, Route, Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence, useScroll, useTransform, useSpring } from "framer-motion";
import {
    Gamepad2, Trophy, Users, MapPin,
    Phone, ExternalLink, ChevronRight, ChevronLeft,
    Zap, Star,
    Youtube, Instagram, Facebook, X
} from "lucide-react";
import "./index.css";

// Import local images
import image1 from "./assets/images/image1.jpeg";
import image2 from "./assets/images/image2.jpeg";
import image4 from "./assets/images/image4.jpeg";
import image5 from "./assets/images/image5.jpeg";
import image6 from "./assets/images/image6.jpeg";
import image9 from "./assets/images/image9.jpeg";
import image10 from "./assets/images/image10.jpeg";
import image11 from "./assets/images/image11.jpeg";
import image12 from "./assets/images/image12.jpeg";
import image13 from "./assets/images/image13.jpeg";
import image14 from "./assets/images/image14.jpeg";
import image15 from "./assets/images/image15.jpeg";
import image16 from "./assets/images/image16.jpeg";
import image17 from "./assets/images/image17.jpeg";
import image18 from "./assets/images/image18.jpeg";
import image19 from "./assets/images/image19.jpeg";
import image20 from "./assets/images/image20.jpeg";
import image21 from "./assets/images/image21.jpeg";
import image22 from "./assets/images/image22.jpeg";
import image23 from "./assets/images/image23.jpeg";
import image24 from "./assets/images/image24.jpeg";
import image25 from "./assets/images/image25.jpg";
import image26 from "./assets/images/image26.jpg";
import image27 from "./assets/images/image27.jpg";
import image28 from "./assets/images/image28.jpg";
import image29 from "./assets/images/image29.jpg";
import image30 from "./assets/images/image30.jpg";
import jal1 from "./assets/images/jal1.jpeg";
import jal2 from "./assets/images/jal2.jpeg";
import jal3 from "./assets/images/jal3.jpeg";
import jal from "./assets/images/jal.jpeg";
import racingsim from "./assets/images/racingsim.jpeg";
import tournament from "./assets/images/tournament.png";
import winners from "./assets/images/tournament.png";
import food from "./assets/images/food.png";
import parties from "./assets/images/parties.png";
import pitchDeck from "./assets/docs/The Gaming Theory-Pitch deck.pdf";

// Flagship store (Chandigarh) images
import chdThumb from "./assets/images/flagship/VS_00513.jpg.jpeg";
import chd1 from "./assets/images/flagship/IMG_8136.JPG.jpeg";
import chd2 from "./assets/images/flagship/VS_00514.jpg.jpeg";
import chd3 from "./assets/images/flagship/VS_00516.jpg.jpeg";
import chd4 from "./assets/images/flagship/VS_00523.jpg.jpeg";
import chd5 from "./assets/images/flagship/VS_00555.jpg.jpeg";
import chd6 from "./assets/images/flagship/VS_00562.jpg.jpeg";
import chd7 from "./assets/images/flagship/VS_00574.jpg.jpeg";
import chd8 from "./assets/images/flagship/VS_00581.jpg.jpeg";
import chd9 from "./assets/images/flagship/VS_00588.jpg.jpeg";
/* ------------------------------------------------------------------
   AMBIENT COMPONENTS
------------------------------------------------------------------ */

function GridBackground() {
    return (
        <>
            <div className="ambient-grid" />
            <div className="scanlines" />
            <div className="ambient-particles" />
        </>
    );
}

function Preloader({ onComplete }) {
    const [status, setStatus] = useState("Initializing Cores...");

    useEffect(() => {
        const sequence = [
            { t: 0, s: "Initializing Cores..." },
            { t: 600, s: "Loading Arena Data..." },
            { t: 1200, s: "Syncing with ESports Hub..." },
            { t: 1800, s: "System Ready." },
        ];

        sequence.forEach(({ t, s }) => {
            setTimeout(() => setStatus(s), t);
        });

        const timer = setTimeout(onComplete, 2600);
        return () => clearTimeout(timer);
    }, [onComplete]);

    return (
        <motion.div
            className="preloader"
            initial={{ opacity: 1 }}
            exit={{ opacity: 0, scale: 1.1 }}
            transition={{ duration: 0.8, ease: "easeInOut" }}
        >
            <div className="preloader-hud preloader-hud-1" />
            <div className="preloader-hud preloader-hud-2" />
            <div className="preloader-content">
                <div className="preloader-logo">
                    THE<span className="accent">GAMING</span>THEORY
                </div>
                <div className="preloader-bar-wrap">
                    <div className="preloader-bar-inner" />
                </div>
                <div className="preloader-status">
                    <span>{status}</span>
                    <span>{Math.floor(Math.random() * 100)}%</span>
                </div>
            </div>
        </motion.div>
    );
}



function TiltCard({ children, className = "" }) {
    const ref = useRef(null);
    const x = useSpring(0, { stiffness: 120, damping: 30 });
    const y = useSpring(0, { stiffness: 120, damping: 30 });

    const handleMouse = (e) => {
        if (!ref.current || window.matchMedia("(pointer: coarse)").matches) return;
        const rect = ref.current.getBoundingClientRect();
        const xPct = ((e.clientX - rect.left) / rect.width - 0.5) * 16;
        const yPct = ((e.clientY - rect.top) / rect.height - 0.5) * -16;
        x.set(xPct);
        y.set(yPct);
    };

    const handleLeave = () => { x.set(0); y.set(0); };

    return (
        <motion.div
            ref={ref}
            onMouseMove={handleMouse}
            onMouseLeave={handleLeave}
            style={{ rotateX: y, rotateY: x, transformStyle: "preserve-3d" }}
            className={`tilt-card ${className}`}
        >
            <div className="tilt-content">{children}</div>
        </motion.div>
    );
}

/* ------------------------------------------------------------------
   HOOKS
------------------------------------------------------------------ */

function useScrollReveal(threshold = 0.15) {
    const ref = useRef(null);
    const [visible, setVisible] = useState(false);
    useEffect(() => {
        const el = ref.current;
        if (!el) return;
        const obs = new IntersectionObserver(
            ([entry]) => { if (entry.isIntersecting) setVisible(true); },
            { threshold }
        );
        obs.observe(el);
        return () => obs.disconnect();
    }, [threshold]);
    return [ref, visible];
}

function useCounter(end, duration = 2000, start = false) {
    const [count, setCount] = useState(0);
    useEffect(() => {
        if (!start) return;
        let raf;
        let startTime = null;
        const step = (ts) => {
            if (!startTime) startTime = ts;
            const p = Math.min((ts - startTime) / duration, 1);
            const ease = 1 - Math.pow(1 - p, 3);
            setCount(Math.floor(ease * end));
            if (p < 1) raf = requestAnimationFrame(step);
        };
        raf = requestAnimationFrame(step);
        return () => cancelAnimationFrame(raf);
    }, [start, end, duration]);
    return count;
}

/* ------------------------------------------------------------------
   NAVBAR
------------------------------------------------------------------ */

function Navbar() {
    const [scrolled, setScrolled] = useState(false);
    const [menuOpen, setMenuOpen] = useState(false);

    const [activeLink, setActiveLink] = useState("home");

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 50);
        window.addEventListener("scroll", onScroll);
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => {
        document.body.style.overflow = menuOpen ? "hidden" : "";
        return () => { document.body.style.overflow = ""; };
    }, [menuOpen]);

    const close = () => setMenuOpen(false);
    const navLinks = [
        { name: "Home", href: "/" },
        { name: "About", href: "/#about" },
        { name: "Experience", href: "/#experience" },
        { name: "Find Us", href: "/#locations" },
        { name: "Events", href: "/#events" },
        { name: "Franchise", href: "/#franchise" },
        { name: "Contact", href: "/#contact" }
    ];

    return (
        <>
            <motion.nav
                className={`navbar ${scrolled ? "scrolled" : ""}`}
                initial={{ y: -80, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ duration: 0.7, ease: "easeOut" }}
            >
                <div className="container nav-inner">
                    <Link to="/" className="nav-logo">
                        THE<span className="accent">GAMING</span>THEORY
                    </Link>

                    <div className="nav-links">
                        {navLinks.map((link) => (
                            <a
                                key={link.name}
                                href={link.href}
                                className={activeLink === link.name.toLowerCase() ? "active" : ""}
                                onClick={() => setActiveLink(link.name.toLowerCase())}
                            >
                                {link.name}
                            </a>
                        ))}
                    </div>

                    <a href="#contact" className="nav-cta-btn">
                        Book a Session <ChevronRight size={14} />
                    </a>

                    <button
                        className={`hamburger ${menuOpen ? "active" : ""}`}
                        onClick={() => setMenuOpen((o) => !o)}
                        aria-label="Toggle menu"
                    >
                        <span /><span /><span />
                    </button>
                </div>
            </motion.nav>

            <AnimatePresence>
                {menuOpen && (
                    <motion.div
                        className="mobile-overlay open"
                        initial={{ opacity: 0, y: -20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -20 }}
                        transition={{ duration: 0.3 }}
                    >
                        <button className="mobile-close" onClick={close} aria-label="Close menu">✕</button>
                        {navLinks.map((link, i) => (
                            <motion.a
                                key={link.name}
                                href={link.href}
                                onClick={close}
                                initial={{ opacity: 0, x: -20 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ delay: i * 0.05 }}
                            >
                                {link.name}
                            </motion.a>
                        ))}
                        <a href="#contact" className="nav-cta-btn" onClick={close}>
                            Book a Session
                        </a>
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    );
}

/* ------------------------------------------------------------------
   HERO
------------------------------------------------------------------ */

function StatItem({ end, suffix = "+", label, icon, decimal = false }) {
    const [ref, visible] = useScrollReveal(0.3);
    const count = useCounter(end, 2200, visible);
    return (
        <div className="stat-item" ref={ref}>
            <div className="stat-icon">{icon}</div>
            <div className="stat-number">{decimal ? (count / 10).toFixed(1) : count}{suffix}</div>
            <div className="stat-label">{label}</div>
        </div>
    );
}

function Hero() {
    const { scrollY } = useScroll();
    const bgY = useTransform(scrollY, [0, 600], [0, 120]);

    return (
        <section className="hero" id="home">
            <div className="hero-bg">
                <motion.img
                    src={image1}
                    alt="TheGamingTheory Cafe Interior"
                    style={{ y: bgY }}
                    initial={{ scale: 1.15, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ duration: 1.8, ease: "easeOut" }}
                />
                <div className="hero-bg-overlay" />
            </div>

            <div className="scanning-bar" />
            <div className="hud-corner hud-tl" />
            <div className="hud-corner hud-tr" />
            <div className="hud-corner hud-bl" />
            <div className="hud-corner hud-br" />

            <div className="hero-shapes">
                <motion.div
                    className="shape shape-1"
                    animate={{ y: [0, 40, 0], rotate: [0, 8, 0] }}
                    transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
                />
                <motion.div
                    className="shape shape-2"
                    animate={{ y: [0, -50, 0], rotate: [0, -10, 0] }}
                    transition={{ duration: 13, repeat: Infinity, ease: "easeInOut" }}
                />
                <motion.div
                    className="shape shape-3"
                    animate={{ x: [0, 30, 0], opacity: [0.1, 0.25, 0.1] }}
                    transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
                />
            </div>

            <div className="container hero-content">
                <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.7 }}
                >
                    <span className="hero-tag">
                        <Zap size={14} style={{ color: "var(--c-cyan)" }} />
                        India's Premium Gaming Cafe Chain
                    </span>
                </motion.div>

                <motion.h1
                    className="hero-heading"
                    initial={{ opacity: 0, y: 40 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.9, delay: 0.15 }}
                >
                    <span className="glitch-text" data-text="Play. Compete.">Play. Compete.</span><br />
                    <span className="gradient-text">Dominate.</span>
                </motion.h1>

                <motion.p
                    className="hero-subtext"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.8, delay: 0.35 }}
                >
                    Experience next-level gaming with RTX-powered PCs, PS5 consoles, racing simulators,
                    and epic tournaments — across our cafes in India.
                </motion.p>

                <motion.div
                    className="hero-btns"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 0.55 }}
                >
                    <a href="#locations" className="btn-primary">
                        Find Your Arena <ChevronRight size={18} style={{ marginLeft: 6 }} />
                    </a>
                    <a href="#experience" className="btn-secondary">Explore Experiences</a>
                </motion.div>

                <motion.div
                    className="hero-stats"
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.9, delay: 0.9 }}
                >
                    <StatItem end={5} label="Gaming Cafes" icon={<Gamepad2 size={22} />} />
                    <StatItem end={5} suffix="+" label="Cities" icon={<MapPin size={22} />} />
                    <StatItem end={250} suffix="k+" label="Gamers" icon={<Users size={22} />} />
                    <StatItem end={47} suffix="" label="Google Rating" icon={<Star size={22} />} decimal />
                </motion.div>
            </div>

            <div className="scroll-indicator">
                <div className="mouse"><span className="mouse-wheel" /></div>
                <span>Scroll</span>
            </div>
        </section>
    );
}

/* ------------------------------------------------------------------
   MARQUEE
------------------------------------------------------------------ */

function Marquee() {
    const items = ["CONSOLE GAMING", "PC GAMING", "ESPORTS TOURNAMENTS", "RACING SIM", "SNOOKER", "VR EXPERIENCES", "PREMIUM EXPERIENCE", "COMMUNITY EVENTS", "FOOD & BEVERAGES"];
    const doubled = [...items, ...items];
    return (
        <div className="marquee-section">
            <motion.div
                className="marquee-track"
                animate={{ x: [0, "-50%"] }}
                transition={{ ease: "linear", duration: 30, repeat: Infinity }}
                style={{ animation: "none" }} /* Override CSS animation */
            >
                {doubled.map((item, i) => (
                    <div className="marquee-item" key={i}>
                        <span className="dot" />{item}
                    </div>
                ))}
            </motion.div>
        </div>
    );
}

function MarqueeReverse() {
    const items = ["PLAY • COMPETE • WIN", "PREMIUM SETUPS", "ESPORTS READY", "NEXT-LEVEL GAMING", "THE THEORY", "GAME ON"];
    const doubled = [...items, ...items];
    return (
        <div className="marquee-section">
            <motion.div
                className="marquee-track"
                animate={{ x: ["-50%", 0] }}
                transition={{ ease: "linear", duration: 25, repeat: Infinity }}
                style={{ animation: "none" }} /* Override CSS animation */
            >
                {doubled.map((item, i) => (
                    <div className="marquee-item" key={i}>
                        <span className="dot" />{item}
                    </div>
                ))}
            </motion.div>
        </div>
    );
}

/* ------------------------------------------------------------------
   ABOUT
------------------------------------------------------------------ */

function About() {
    return (
        <section className="section" id="about">
            <div className="container">
                <div className="about-grid">
                    <motion.div
                        className="about-images"
                        initial={{ opacity: 0, x: -50 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.8 }}
                    >
                        <TiltCard className="about-img-main">
                            <img src={image2} alt="TheGamingTheory Cafe" />
                            <div className="about-img-badge">
                                <Trophy size={16} />
                                <span>India's #1 Gaming Cafe Chain</span>
                            </div>
                        </TiltCard>
                        <motion.div
                            className="about-img-float"
                            animate={{ y: [0, -18, 0] }}
                            transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
                            style={{ willChange: "transform" }}
                        >
                            <img src={image6} alt="Gaming Setup" />
                        </motion.div>
                    </motion.div>

                    <div className="about-text">
                        <motion.span
                            className="section-label"
                            initial={{ opacity: 0 }}
                            whileInView={{ opacity: 1 }}
                            viewport={{ once: true }}
                        >Who We Are</motion.span>
                        <motion.div
                            className="accent-line"
                            initial={{ scaleX: 0 }}
                            whileInView={{ scaleX: 1 }}
                            viewport={{ once: true }}
                            style={{ transformOrigin: "left" }}
                        />
                        <motion.h2
                            className="section-title"
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                        >
                            More Than a Cafe.<br />
                            A Gaming <span className="gradient-text">Destination.</span>
                        </motion.h2>
                        <motion.p
                            initial={{ opacity: 0 }}
                            whileInView={{ opacity: 1 }}
                            viewport={{ once: true }}
                            transition={{ delay: 0.2 }}
                        >
                            TheGamingTheory is India's premier gaming cafe chain, built to
                            deliver the ultimate experience for every kind of gamer. From
                            high-end PC rigs and consoles to racing simulators and VR —
                            we've built spaces where gamers truly belong.
                        </motion.p>
                        <motion.p
                            initial={{ opacity: 0 }}
                            whileInView={{ opacity: 1 }}
                            viewport={{ once: true }}
                            transition={{ delay: 0.3 }}
                        >
                            With arenas across multiple cities, we host regular esports
                            tournaments, community nights, and private events. Our flagship
                            cafes set the gold standard for what a gaming space should feel like.
                        </motion.p>


                    </div>
                </div>
            </div>
        </section>
    );
}

/* ------------------------------------------------------------------
   GALLERY (with Lightbox)
------------------------------------------------------------------ */

const galleryImages = [
    { src: chdThumb, title: "Chandigarh Flagship Arena", tags: ["Flagship", "Chandigarh", "VIP"] },
    { src: chd1, title: "Chandigarh Pro Lounge", tags: ["Flagship", "RTX 4090"] },
    { src: chd2, title: "Chandigarh Cyber Station", tags: ["Flagship", "240Hz"] },
    { src: chd3, title: "Chandigarh Gaming Zone", tags: ["Flagship", "RGB"] },
    { src: chd4, title: "Chandigarh VIP Setup", tags: ["Flagship", "Console"] },
    { src: chd5, title: "Chandigarh Streamer Pod", tags: ["Flagship", "Creator"] },
    { src: chd6, title: "Chandigarh Battle Deck", tags: ["Flagship", "Esports"] },
    { src: chd7, title: "Chandigarh Elite Arena", tags: ["Flagship", "Premium"] },
    { src: chd8, title: "Chandigarh High-End Zone", tags: ["Flagship", "VIP"] },
    { src: chd9, title: "Chandigarh Sanctuary", tags: ["Flagship", "Community"] },
    { src: image25, title: "Ludhiana Lounge", tags: ["Premium", "Sleek"] },
    { src: image26, title: "Elite Setup", tags: ["RTX 4090", "VIP"] },
    { src: image27, title: "Next-Gen Arena", tags: ["Console", "PS5"] },
    { src: image28, title: "Cyber Zone", tags: ["Esports", "RGB"] },
    { src: image29, title: "Pro Deck", tags: ["Streaming", "240Hz"] },
    { src: image30, title: "Ludhiana Center", tags: ["Gaming", "Vibe"] },
    { src: image1, title: "Arena View", tags: ["Esports", "Vibe"] },
    { src: jal1, title: "Jalandhar Arena", tags: ["Premium", "PC"] },
    { src: jal2, title: "Pro Setup Jalandhar", tags: ["RTX 4090", "240Hz"] },
    { src: jal3, title: "Jalandhar Hub", tags: ["Gaming", "Community"] },
    { src: image24, title: "Night Mode", tags: ["Premium", "PC"] },
    { src: image4, title: "Pro Setup", tags: ["RTX 4090", "240Hz"] },
    { src: image5, title: "Streaming Pods", tags: ["Creator", "4K"] },
    { src: image11, title: "Neon Alley", tags: ["Aesthetic", "RGB"] },
    { src: tournament, title: "Tournament Zone", tags: ["Competition"] },
    { src: image19, title: "The Pit", tags: ["Intense", "Focus"] },
    { src: image9, title: "Console Hub", tags: ["PlayStation", "Xbox"] },
    { src: image10, title: "VR Pods", tags: ["Meta", "Virtual"] },
    { src: image20, title: "Cyber Deck", tags: ["Modern", "Tech"] },
    { src: image21, title: "Battle Station", tags: ["High-End"] },
    { src: image22, title: "The Sanctuary", tags: ["Gamer Den"] },
    { src: image23, title: "Elite Zone", tags: ["VIP"] },
    { src: image6, title: "Refreshment Bar", tags: ["Cafe", "Fuel"] },
];

function Gallery() {
    const [lightbox, setLightbox] = useState(null);
    const [currentSlide, setCurrentSlide] = useState(0);
    const [isPaused, setIsPaused] = useState(false);
    const [slidesPerView, setSlidesPerView] = useState(3);
    const touchStartX = useRef(0);

    // Responsive slides per view
    useEffect(() => {
        const update = () => {
            if (window.innerWidth <= 640) setSlidesPerView(1);
            else if (window.innerWidth <= 1024) setSlidesPerView(2);
            else setSlidesPerView(3);
        };
        update();
        window.addEventListener("resize", update);
        return () => window.removeEventListener("resize", update);
    }, []);

    const maxSlide = Math.max(0, galleryImages.length - slidesPerView);

    // Clamp current slide on resize
    useEffect(() => {
        if (currentSlide > maxSlide) setCurrentSlide(maxSlide);
    }, [maxSlide, currentSlide]);

    const slidePrev = useCallback(() => setCurrentSlide((s) => (s > 0 ? s - 1 : maxSlide)), [maxSlide]);
    const slideNext = useCallback(() => setCurrentSlide((s) => (s < maxSlide ? s + 1 : 0)), [maxSlide]);

    // Auto-play
    useEffect(() => {
        if (isPaused) return;
        const timer = setInterval(() => {
            setCurrentSlide((s) => (s < maxSlide ? s + 1 : 0));
        }, 4000);
        return () => clearInterval(timer);
    }, [isPaused, maxSlide]);

    // Lightbox navigation
    const lbPrev = useCallback(() => setLightbox((i) => (i > 0 ? i - 1 : galleryImages.length - 1)), []);
    const lbNext = useCallback(() => setLightbox((i) => (i < galleryImages.length - 1 ? i + 1 : 0)), []);

    useEffect(() => {
        const onKey = (e) => {
            if (lightbox === null) return;
            if (e.key === "ArrowLeft") lbPrev();
            if (e.key === "ArrowRight") lbNext();
            if (e.key === "Escape") setLightbox(null);
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [lightbox, lbPrev, lbNext]);

    const handleTouchStart = (e) => { touchStartX.current = e.changedTouches[0].screenX; };
    const handleTouchEnd = (e) => {
        const diff = touchStartX.current - e.changedTouches[0].screenX;
        if (Math.abs(diff) > 50) { diff > 0 ? slideNext() : slidePrev(); }
    };

    return (
        <section className="section" id="gallery">
            <div className="container">
                <motion.div
                    style={{ textAlign: "center" }}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                >
                    <span className="section-label">Our Arenas</span>
                    <div className="accent-line" style={{ margin: "0 auto 24px" }} />
                    <h2 className="section-title">
                        Step Inside the <span className="gradient-text">Arena</span>
                    </h2>
                    <p className="section-sub" style={{ margin: "0 auto 48px" }}>
                        Every zone designed for maximum performance and immersion.
                        Click any photo to explore.
                    </p>
                </motion.div>

                <div
                    className="gallery-slider"
                    onMouseEnter={() => setIsPaused(true)}
                    onMouseLeave={() => setIsPaused(false)}
                    onTouchStart={handleTouchStart}
                    onTouchEnd={handleTouchEnd}
                >
                    <div
                        className="gallery-slider-track"
                        style={{ transform: `translateX(-${currentSlide * (100 / slidesPerView)}%)` }}
                    >
                        {galleryImages.map((img, i) => (
                            <div
                                className="gallery-slide"
                                key={i}
                                style={{ flex: `0 0 ${100 / slidesPerView}%` }}
                                onClick={() => setLightbox(i)}
                                data-hover="true"
                            >
                                <img src={img.src} alt={img.title} loading="lazy" />
                                <div className="gallery-slide-overlay">
                                    <h4>{img.title}</h4>
                                    <div className="gallery-tags">
                                        {img.tags.map((tag, j) => (
                                            <span key={j} className="gallery-tag">{tag}</span>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                    <button className="slider-btn slider-prev" onClick={slidePrev} aria-label="Previous"><ChevronLeft size={24} /></button>
                    <button className="slider-btn slider-next" onClick={slideNext} aria-label="Next"><ChevronRight size={24} /></button>
                    <div className="slider-counter">
                        {currentSlide + 1} / {galleryImages.length}
                    </div>
                </div>
            </div>

            {/* Lightbox */}
            <AnimatePresence>
                {lightbox !== null && (
                    <motion.div
                        className="lightbox-overlay"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={() => setLightbox(null)}
                    >
                        <motion.div
                            className="lightbox-inner"
                            initial={{ scale: 0.85, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            exit={{ scale: 0.85, opacity: 0 }}
                            onClick={(e) => e.stopPropagation()}
                        >
                            <img src={galleryImages[lightbox].src} alt={galleryImages[lightbox].title} />
                            <div className="lightbox-caption">
                                <h4>{galleryImages[lightbox].title}</h4>
                                <div className="gallery-tags">
                                    {galleryImages[lightbox].tags.map((tag, j) => (
                                        <span key={j} className="gallery-tag">{tag}</span>
                                    ))}
                                </div>
                            </div>
                            <button className="lightbox-btn lightbox-prev" onClick={lbPrev} aria-label="Previous"><ChevronLeft size={24} /></button>
                            <button className="lightbox-btn lightbox-next" onClick={lbNext} aria-label="Next"><ChevronRight size={24} /></button>
                            <button className="lightbox-btn lightbox-close" onClick={() => setLightbox(null)} aria-label="Close"><X size={20} /></button>
                            <div className="lightbox-counter">{lightbox + 1} / {galleryImages.length}</div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </section>
    );
}

/* ------------------------------------------------------------------
   EXPERIENCE
------------------------------------------------------------------ */

const experiences = [
    { icon: "🎮", title: "Console Gaming", desc: "PS5, Xbox Series X, and more — latest consoles with premium displays and comfortable seating.", img: image12 },
    { icon: "🖥️", title: "High-End PC Gaming", desc: "RTX-powered rigs, 240Hz monitors, mechanical keyboards — built for competitive gaming.", img: image13 },
    { icon: "🏎️", title: "Racing Simulators", desc: "Full racing simulator setups with force feedback wheels and immersive triple-screen rigs.", img: racingsim },
    { icon: "🍕", title: "Food & Beverages", desc: "Fuel your sessions with our curated menu of gamer snacks, energy drinks, and full meals.", img: food, imgClass: "img-food" },
    { icon: "🏆", title: "Tournaments", desc: "Regular esports tournaments, community nights, and prize pools that keep you coming back.", img: winners, imgClass: "img-tournaments" },
    { icon: "🎉", title: "Private Parties", desc: "Book the entire venue or a private zone for birthdays, team outings, and corporate events.", img: parties, imgClass: "img-parties" },
];

function Experience() {
    return (
        <section className="section exp-section" id="experience">
            <div className="container">
                <div style={{ textAlign: "center", marginBottom: 64 }}>
                    <motion.span className="section-label" initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }}>
                        What We Offer
                    </motion.span>
                    <motion.div className="accent-line" style={{ margin: "0 auto 24px", transformOrigin: "center" }} initial={{ scaleX: 0 }} whileInView={{ scaleX: 1 }} viewport={{ once: true }} />
                    <motion.h2 className="section-title" initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
                        The Ultimate Gaming <span className="gradient-text">Experience</span>
                    </motion.h2>
                </div>

                <motion.div
                    className="exp-grid"
                    initial="hidden"
                    whileInView="show"
                    viewport={{ once: true, margin: "-80px" }}
                    variants={{
                        hidden: { opacity: 0 },
                        show: { opacity: 1, transition: { staggerChildren: 0.1 } }
                    }}
                >
                    {experiences.map((exp, i) => (
                        <motion.div
                            key={i}
                            variants={{
                                hidden: { opacity: 0, y: 40, scale: 0.95 },
                                show: { opacity: 1, y: 0, scale: 1, transition: { type: "spring", stiffness: 100, damping: 20 } }
                            }}
                        >
                            <TiltCard className="exp-card">
                                <div className="exp-card-img">
                                    <img src={exp.img} alt={exp.title} loading="lazy" className={exp.imgClass || ""} />
                                    <div className="exp-card-icon-badge">{exp.icon}</div>
                                </div>
                                <div className="exp-card-body">
                                    <h3>{exp.title}</h3>
                                    <p>{exp.desc}</p>
                                </div>
                            </TiltCard>
                        </motion.div>
                    ))}
                </motion.div>
            </div>
        </section>
    );
}

/* ------------------------------------------------------------------
   EVENTS & TOURNAMENTS
------------------------------------------------------------------ */

const tournaments = [
    { icon: "🎯", title: "Valorant Showdown", game: "Valorant", format: "5v5", status: "Upcoming", desc: "Compete in tactical 5v5 battles. Show your team's strategy and aim in our regular Valorant tournaments." },
    { icon: "⚽", title: "FIFA Championship", game: "EA FC 25", format: "1v1", status: "Monthly", desc: "The ultimate football gaming showdown. Prove you're the best FIFA player in the city." },
    { icon: "🔫", title: "CS2 Tactical Cup", game: "Counter-Strike 2", format: "5v5", status: "Upcoming", desc: "Classic competitive FPS action. Assemble your squad and dominate the server." },
    { icon: "🥊", title: "Tekken Arena", game: "Tekken 8", format: "1v1", status: "Weekly", desc: "Enter the King of Iron Fist tournament. Master your combos and claim victory." },
];

function Events() {
    return (
        <section className="section events-section" id="events">
            <div className="container">
                <div style={{ textAlign: "center", marginBottom: 64 }}>
                    <motion.span className="section-label" initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }}>
                        Events & Tournaments
                    </motion.span>
                    <motion.div className="accent-line" style={{ margin: "0 auto 24px", transformOrigin: "center" }} initial={{ scaleX: 0 }} whileInView={{ scaleX: 1 }} viewport={{ once: true }} />
                    <motion.h2 className="section-title" initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
                        Where Legends <span className="gradient-text">Compete</span>
                    </motion.h2>
                    <motion.p className="section-sub" style={{ margin: "0 auto" }} initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} transition={{ delay: 0.2 }}>
                        Regular esports tournaments with exciting prize pools. Compete with the best gamers in your city.
                    </motion.p>
                </div>

                <motion.div
                    className="tournament-grid"
                    initial="hidden"
                    whileInView="show"
                    viewport={{ once: true, margin: "-50px" }}
                    variants={{ hidden: { opacity: 0 }, show: { opacity: 1, transition: { staggerChildren: 0.1 } } }}
                >
                    {tournaments.map((t, i) => (
                        <motion.div key={i} variants={{ hidden: { opacity: 0, y: 24 }, show: { opacity: 1, y: 0 } }}>
                            <TiltCard className="tournament-card">
                                <div className="tournament-card-header">
                                    <div className="tournament-icon">{t.icon}</div>
                                    <div>
                                        <h3>{t.title}</h3>
                                        <span className="game-name">{t.game}</span>
                                    </div>
                                </div>
                                <p className="tournament-desc">{t.desc}</p>
                                <div className="tournament-meta">
                                    <span><Users size={14} /> {t.format}</span>
                                    <span><Trophy size={14} /> {t.status}</span>
                                </div>
                                {/* <a href="#contact" className="tournament-register">
                                    Coming Soon <ChevronRight size={16} />
                                </a> */}
                            </TiltCard>
                        </motion.div>
                    ))}
                </motion.div>

                <motion.div
                    className="events-cta"
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.3 }}
                >
                    <a href="https://www.instagram.com/thegamingtheory/" target="_blank" rel="noreferrer" className="btn-primary">
                        Follow for Updates <ChevronRight size={18} style={{ marginLeft: 6 }} />
                    </a>
                    <a href="#contact" className="btn-secondary">Register for Tournament</a>
                </motion.div>
            </div>
        </section>
    );
}

/* ------------------------------------------------------------------
   FRANCHISE (Compact CTA)
------------------------------------------------------------------ */

const compactBenefits = [
    { icon: "📊", title: "Proven Model", desc: "Battle-tested operations" },
    { icon: "💻", title: "Proprietary Tech", desc: "Custom management software" },
    { icon: "🎓", title: "Full Training", desc: "Complete staff training" },
    { icon: "🤝", title: "Ongoing Support", desc: "Dedicated success team" },
];

function FranchiseCTA() {
    return (
        <section className="section" id="franchise">
            <div className="container">
                <motion.div
                    className="franchise-cta"
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.7 }}
                >
                    <span className="section-label">Franchise</span>
                    <div className="accent-line" style={{ margin: "0 auto 24px" }} />
                    <h3>Want to Own a <span className="gradient-text">TheGamingTheory?</span></h3>
                    <p style={{ color: "rgba(230,230,230,0.6)", maxWidth: 600, margin: "0 auto 40px", lineHeight: 1.7 }}>
                        Turn your passion for gaming into a thriving business. We provide end-to-end setup, proprietary technology, and ongoing support to help you succeed.
                    </p>

                    <div className="franchise-benefits-row">
                        {compactBenefits.map((b, i) => (
                            <div key={i} className="franchise-benefit-mini">
                                <div className="fb-icon">{b.icon}</div>
                                <h4>{b.title}</h4>
                                <p>{b.desc}</p>
                            </div>
                        ))}
                    </div>

                    <div className="franchise-cta-btns">
                        <a href="#contact" className="btn-primary">Apply for Franchise</a>
                        <a href={pitchDeck} download="The Gaming Theory-Pitch deck.pdf" target="_blank" rel="noreferrer" className="btn-outline">
                            Download Brochure
                        </a>
                    </div>
                </motion.div>
            </div>
        </section>
    );
}

/* ------------------------------------------------------------------
   TESTIMONIALS
------------------------------------------------------------------ */

const testimonials = [
    {
        text: "Great games. Great experience. The vibes so cool. Hospitality is nice.. everyone just loves to be here.",
        name: "Rohan Jindal", role: "Local Guide · 24 reviews", stars: 5, avatar: image12,
    },
    {
        text: "One of the Best Gaming places I've ever been too!!! It has friendly atmosphere along with high specs PCs and PS.",
        name: "Simon Riley", role: "4 reviews", stars: 5, avatar: image13,
    },
    {
        text: "I recently visited The Gaming Theory, and it was a great experience overall. The place is well-maintained, with a clean and comfortable gaming environment. The variety of games available is impressive — ranging from popular console titles and high-performance PC setups to arcade classics.",
        name: "Vikrant Adhav", role: "Google Reviewer", stars: 5, avatar: image14,
    },
    {
        text: "Loved it I always come at saturdays or holidays. The pc's r very powerful for almost all the games. Loved it recommended for gamers like me.",
        name: "Sumit Khajuria", role: "Local Guide · 40 reviews", stars: 5, avatar: image15,
    },
    {
        text: "One of the best gaming experiences I've had! Incredible setup — the pc's are absolute beasts, the vibe is on point, and the whole place just screams pro-level gaming. Had a brief chat with the owner too — super friendly guy, and very passionate about gaming. 100% recommended! 🔥",
        name: "Savi Mangotra", role: "5 reviews", stars: 5, avatar: image16,
    },
    {
        text: "Had an amazing time at TGT! The atmosphere is spot on — perfect mix of energy and comfort. The equipment is high-spec and runs everything flawlessly, and the staff really go the extra mile. Easily a go-to spot for any gamer. Highly recommend!",
        name: "Jayesh Sanap", role: "Google Reviewer", stars: 5, avatar: image17,
    },
    {
        text: "What a place, heaven for gamers!!! If you have not visited yet, you're missing out on elite gaming experience.",
        name: "Anmol Mehra", role: "4 reviews", stars: 5, avatar: image18,
    },
    {
        text: "Absolutely loved the vibe at TGT! The ambience is perfect. Whether you're here to grind ranked matches or just chill with your squad - TGT is the place to be. The setup is top-notch, the PCs are high-end, and the staff is super friendly. 10/10 would recommend!",
        name: "Harshit", role: "6 reviews", stars: 5, avatar: image19,
    },
    {
        text: "The best gaming experience I've had in a while. The tech, management is awesome and the crowd is worth interacting as well. They also manage tournaments with amazing cash prices. Must visit IMO ✨",
        name: "Haroon Teji", role: "7 reviews", stars: 5, avatar: image20,
    },
];

function Testimonials() {
    const [slide, setSlide] = useState(0);

    const goTo = useCallback(
        (dir) => setSlide((s) => (s + dir + testimonials.length) % testimonials.length),
        []
    );

    return (
        <section className="section testimonials-section">
            <div className="container">
                <motion.div style={{ textAlign: "center" }} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
                    <span className="section-label">Testimonials</span>
                    <div className="accent-line" style={{ margin: "0 auto 24px" }} />
                    <h2 className="section-title">
                        What People Are <span className="gradient-text">Saying</span>
                    </h2>
                    <a
                        href="https://www.google.com/search?q=The+Gaming+Theory+Reviews"
                        target="_blank" rel="noreferrer"
                        className="review-link"
                    >
                        <Star size={14} /> Read All Google Reviews →
                    </a>
                </motion.div>

                <div className="testimonials-wrapper">
                    <AnimatePresence mode="wait">
                        <motion.div
                            key={slide}
                            className="testimonial-card featured"
                            initial={{ opacity: 0, x: 40 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0, x: -40 }}
                            transition={{ duration: 0.4 }}
                        >
                            <div className="tc-quote-mark">"</div>
                            <p className="tc-text">{testimonials[slide].text}</p>
                            <div className="tc-stars">
                                {Array.from({ length: testimonials[slide].stars }).map((_, j) => (
                                    <Star key={j} size={14} fill="var(--c-cyan)" color="var(--c-cyan)" />
                                ))}
                            </div>
                            <div className="tc-author">
                                <div className="tc-avatar">
                                    <img src={testimonials[slide].avatar} alt={testimonials[slide].name} />
                                </div>
                                <div>
                                    <div className="tc-name">{testimonials[slide].name}</div>
                                    <div className="tc-role">{testimonials[slide].role}</div>
                                </div>
                            </div>
                        </motion.div>
                    </AnimatePresence>

                    <div className="testimonials-controls">
                        <button className="t-control" onClick={() => goTo(-1)} aria-label="Previous">
                            <ChevronLeft size={20} />
                        </button>
                        <div className="t-dots">
                            {testimonials.map((_, i) => (
                                <button
                                    key={i}
                                    className={`t-dot ${i === slide ? "active" : ""}`}
                                    onClick={() => setSlide(i)}
                                    aria-label={`Go to slide ${i + 1}`}
                                />
                            ))}
                        </div>
                        <button className="t-control" onClick={() => goTo(1)} aria-label="Next">
                            <ChevronRight size={20} />
                        </button>
                    </div>
                </div>
            </div>
        </section>
    );
}

/* ------------------------------------------------------------------
   LOCATIONS
------------------------------------------------------------------ */

const locations = [
    {
        city: "Chandigarh",
        address: "SCO: 220, Second Floor, Sector: Market, Sector 36-D, Sector 36, Chandigarh, 160036",
        phone: "9878893865",
        type: "flagship",
        img: chdThumb,
        mapUrl: "https://maps.app.goo.gl/SoR4zxUAKrXYijqp9",
        hours: "Mon–Sun: 10 AM – 6 AM",
    },
    {
        city: "Kharar (Punjab)",
        address: "1st Floor, Ek onkar city, SCO No. 3, Ludhiana - Chandigarh NH, Kharar, Punjab 140306",
        phone: "098784 83865",
        type: "branch",
        img: image5,
        mapUrl: "https://share.google/jTgSY32Rsv4zeVUdo",
        hours: "Mon–Sun: 10 AM – 6 AM",
    },
    {
        city: "Jalandhar",
        address: "S.C.O 7-8, 1st Floor, Green Valley Commercial Complex, Vill. Maheru, Tehsil Phagwara, Distt Kapurthalla, 144401",
        phone: "7006700120",
        type: "branch",
        img: jal,
        mapUrl: "https://www.google.com/maps/search/The+Gaming+Theory+Jalandhar",
        hours: "Mon–Sun: 10 AM – 6 AM",
    },
    {
        city: "Ludhiana",
        address: "#B-29-3402/1089/2, Second floor, Above The Murash, Sat Paul Mittal Rd, opp. Welkom Palace, Gurdev Nagar, Ludhiana, Punjab 141001",
        phone: "62394 17305",
        type: "new",
        img: image25,
        mapUrl: "https://www.google.com/maps/search/?api=1&query=The+Gaming+Theory+Ludhiana",
        hours: "Mon–Sun: 10 AM – 6 AM",
    },
];

function Locations() {
    const isLive = () => {
        const hour = new Date().getHours();
        return hour >= 10 || hour < 6;
    };

    return (
        <section className="section locations-section" id="locations">
            <div className="container">
                <motion.div style={{ textAlign: "center", marginBottom: 48 }} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
                    <span className="section-label">Find Us</span>
                    <div className="accent-line" style={{ margin: "0 auto 24px" }} />
                    <h2 className="section-title">
                        Find Us <span className="gradient-text">Near You</span>
                    </h2>
                    <p className="section-sub" style={{ margin: "0 auto 16px" }}>
                        Premium gaming arenas across India. Visit your nearest TheGamingTheory.
                    </p>
                </motion.div>

                <motion.div
                    className="locations-grid"
                    initial="hidden"
                    whileInView="show"
                    viewport={{ once: true }}
                    variants={{
                        hidden: { opacity: 0 },
                        show: { opacity: 1, transition: { staggerChildren: 0.2 } }
                    }}
                >
                    {locations.map((loc, i) => (
                        <motion.div
                            key={i}
                            className="location-card"
                            variants={{
                                hidden: { opacity: 0, x: -30 },
                                show: { opacity: 1, x: 0, transition: { duration: 0.6 } }
                            }}
                        >
                            <div className="location-card-img">
                                <img src={loc.img} alt={`TGT ${loc.city}`} loading="lazy" />
                                <span className={`location-badge badge-${loc.type}`}>{loc.type}</span>
                                <div className={`live-tag ${isLive() ? "active" : ""}`}>
                                    <span className="live-dot" /> {isLive() ? "LIVE NOW" : "OPEN AT 10AM"}
                                </div>
                            </div>
                            <div className="location-card-body">
                                <h4>{loc.city}</h4>
                                <div className="loc-detail">
                                    <MapPin size={14} />
                                    <p>{loc.address}</p>
                                </div>
                                <div className="loc-detail">
                                    <Phone size={14} />
                                    <p>{loc.phone}</p>
                                </div>
                                <div className="loc-detail">
                                    <span style={{ fontSize: 14 }}>🕐</span>
                                    <p>{loc.hours}</p>
                                </div>
                                <a href={loc.mapUrl} target="_blank" rel="noreferrer" className="loc-directions">
                                    Get Directions <ExternalLink size={14} />
                                </a>
                            </div>
                        </motion.div>
                    ))}
                </motion.div>
            </div>
        </section>
    );
}

/* ------------------------------------------------------------------
   COMMUNITY
------------------------------------------------------------------ */

const socials = [
    { icon: <Instagram size={22} />, name: "Instagram", handle: "@thegamingtheory", color: "#E1306C", link: "https://www.instagram.com/thegamingtheory/" },
    { icon: <Youtube size={22} />, name: "YouTube", handle: "TheGamingTheory", color: "#FF0000", link: "https://youtube.com/@thegamingtheorykharar?si=lm5WEbyo_CJiWWrO" },
    { icon: <Facebook size={22} />, name: "Facebook", handle: "TheGamingTheory", color: "#1877F2", link: "https://www.facebook.com/thegamingthery" },
];

function Community() {
    return (
        <section className="section community-section" id="community">
            <div className="container">
                <motion.div style={{ textAlign: "center" }} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
                    <span className="section-label">Join The Tribe</span>
                    <div className="accent-line" style={{ margin: "0 auto 24px" }} />
                    <h2 className="section-title">Connect with the <span className="gradient-text">Community</span></h2>
                    <p className="section-sub" style={{ margin: "0 auto 56px" }}>
                        250,000+ gamers strong. Follow us for updates, drops, and tournament announcements.
                    </p>
                </motion.div>

                <motion.div
                    className="social-grid"
                    initial="hidden"
                    whileInView="show"
                    viewport={{ once: true }}
                    variants={{ hidden: { opacity: 0 }, show: { opacity: 1, transition: { staggerChildren: 0.1 } } }}
                >
                    {socials.map((s, i) => (
                        <motion.a
                            href={s.link}
                            key={i}
                            className="social-card"
                            variants={{ hidden: { opacity: 0, y: 20 }, show: { opacity: 1, y: 0 } }}
                            whileHover={{ y: -6, boxShadow: `0 12px 40px ${s.color}33` }}
                            target="_blank"
                            rel="noreferrer"
                        >
                            <div className="social-card-icon" style={{ "--s-color": s.color }}>
                                {s.icon}
                            </div>
                            <h4>{s.name}</h4>
                            <p>{s.handle}</p>
                        </motion.a>
                    ))}
                </motion.div>
            </div>
        </section>
    );
}

/* ------------------------------------------------------------------
   CONTACT
------------------------------------------------------------------ */

function Contact() {
    const [formData, setFormData] = useState({ name: "", email: "", phone: "", subject: "General Inquiry", message: "" });
    const [sent, setSent] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    const handleChange = (e) => setFormData((f) => ({ ...f, [e.target.name]: e.target.value }));
    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            const response = await fetch("https://formspree.io/f/xwvwdywl", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(formData)
            });

            if (response.ok) {
                setSent(true);
                setTimeout(() => setSent(false), 5000);
                setFormData({ name: "", email: "", phone: "", subject: "General Inquiry", message: "" });
            } else {
                alert("Something went wrong. Please try again or contact us directly.");
            }
        } catch (error) {
            alert("Error submitting form. Please check your internet connection.");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <section className="section contact-section" id="contact">
            <div className="container">
                <motion.div style={{ textAlign: "center", marginBottom: 64 }} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
                    <span className="section-label">Get In Touch</span>
                    <div className="accent-line" style={{ margin: "0 auto 24px" }} />
                    <h2 className="section-title">
                        Let's Talk <span className="gradient-text">Theory</span>
                    </h2>
                </motion.div>

                <div className="contact-grid">
                    <motion.div
                        initial={{ opacity: 0, x: -40 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true }}
                    >
                        <p className="contact-intro">
                            Want to book a session, host a tournament, or just say hello?
                            Our team is ready to help you level up.
                        </p>

                        <div className="contact-details">
                            <TiltCard className="detail-item">
                                <div className="detail-icon">📧</div>
                                <div>
                                    <h4>Email Us</h4>
                                    <a href="mailto:info@thegamingtheory.com">info@thegamingtheory.com</a>
                                </div>
                            </TiltCard>
                            <TiltCard className="detail-item">
                                <div className="detail-icon">📞</div>
                                <div>
                                    <h4>Call Us</h4>
                                    <a href="tel:+919878483865">098784 83865</a>
                                </div>
                            </TiltCard>
                            <TiltCard className="detail-item">
                                <div className="detail-icon">📍</div>
                                <div>
                                    <h4>Head Office</h4>
                                    <p>1st Floor, Ek onkar city, SCO No. 3, NH, Kharar, Punjab 140306</p>
                                </div>
                            </TiltCard>
                            <TiltCard className="detail-item">
                                <div className="detail-icon">🕐</div>
                                <div>
                                    <h4>Working Hours</h4>
                                    <p>Mon – Sun: 10 AM – 6 AM (Overnight)</p>
                                </div>
                            </TiltCard>
                        </div>
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0, x: 40 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true }}
                    >
                        <form className="contact-form" onSubmit={handleSubmit}>
                            <div className="form-row">
                                <div className="form-group">
                                    <label>Full Name</label>
                                    <input
                                        type="text" name="name" value={formData.name}
                                        onChange={handleChange} required
                                    />
                                </div>
                                <div className="form-group">
                                    <label>Email Address</label>
                                    <input
                                        type="email" name="email" value={formData.email}
                                        onChange={handleChange} required
                                    />
                                </div>
                            </div>
                            <div className="form-row">
                                <div className="form-group">
                                    <label>Phone Number</label>
                                    <input
                                        type="tel" name="phone" value={formData.phone}
                                        onChange={handleChange} required
                                    />
                                </div>
                                <div className="form-group">
                                    <label>Subject</label>
                                    <select name="subject" value={formData.subject} onChange={handleChange}>
                                        <option>General Inquiry</option>
                                        <option>Book a Session</option>
                                        <option>Tournament Inquiry</option>
                                        <option>Event Booking</option>
                                        <option>Franchise Opportunity</option>
                                        <option>Technical Support</option>
                                    </select>
                                </div>
                            </div>
                            <div className="form-group">
                                <label>Message</label>
                                <textarea
                                    rows="5" name="message" value={formData.message}
                                    onChange={handleChange} placeholder="Tell us how we can help..."
                                />
                            </div>
                            <button type="submit" className="btn-primary form-submit" disabled={submitting}>
                                {submitting ? "Sending..." : sent ? "✓ Message Sent!" : <><Zap size={16} /> Send Message</>}
                            </button>
                        </form>
                    </motion.div>
                </div>
            </div>
        </section>
    );
}

/* ------------------------------------------------------------------
   FOOTER
------------------------------------------------------------------ */

function Footer() {
    const footerSocials = [
        { icon: <Instagram size={18} />, link: "https://www.instagram.com/thegamingtheory/" },
        { icon: <Youtube size={18} />, link: "https://youtube.com/@thegamingtheorykharar?si=lm5WEbyo_CJiWWrO" },
        { icon: <Facebook size={18} />, link: "https://www.facebook.com/thegamingthery" },
    ];

    return (
        <footer className="footer">
            <div className="container">
                <div className="footer-grid">
                    <motion.div className="footer-brand" initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
                        <div className="footer-logo">
                            THE<span className="accent">GAMING</span>THEORY
                        </div>
                        <p>India's premium gaming cafe chain. Delivering the ultimate gaming experience across the country.</p>
                        <div className="footer-socials">
                            {footerSocials.map((s, i) => (
                                <motion.a href={s.link} key={i} className="footer-social-icon" whileHover={{ scale: 1.15, backgroundColor: "var(--c-cyan)", color: "#000" }}>
                                    {s.icon}
                                </motion.a>
                            ))}
                        </div>
                    </motion.div>

                    <motion.div className="footer-col" initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.1 }}>
                        <h4>Quick Links</h4>
                        <a href="#home">Home</a>
                        <a href="#about">About Us</a>
                        <a href="#locations">Find Us</a>
                        <a href="#events">Events</a>
                        <a href="#franchise">Franchise</a>
                        <a href="#contact">Contact Us</a>
                    </motion.div>



                    <motion.div className="footer-col" initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.3 }}>
                        <h4>Contact</h4>
                        <a href="mailto:info@thegamingtheory.com">info@thegamingtheory.com</a>
                        <a href="tel:+919878483865">098784 83865</a>
                        <span className="footer-address">1st Floor, Ek onkar city, SCO No. 3, NH, Kharar, Punjab</span>
                    </motion.div>
                </div>

                <div className="footer-bottom">
                    <p>© {new Date().getFullYear()} TheGamingTheory. All Rights Reserved.</p>
                    <div className="footer-bottom-links">
                        <a href="/privacy">Privacy Policy</a>
                        <a href="/terms">Terms & Conditions</a>
                    </div>
                </div>
            </div>
        </footer>
    );
}



/* ------------------------------------------------------------------
   MAIN LANDING PAGE WRAPPER
------------------------------------------------------------------ */

function LandingPage() {
    return (
        <>
            <Hero />
            <Marquee />
            <About />
            <Experience />
            <Gallery />
            <MarqueeReverse />
            <Events />
            <Testimonials />
            <Locations />
            <FranchiseCTA />
            <Community />
            <Contact />
        </>
    );
}

/* ------------------------------------------------------------------
   APP ROOT with Routing
------------------------------------------------------------------ */

export default function App() {
    // Trigger dev server reload after custom cursor removal
    const [loading, setLoading] = useState(true);
    const { scrollYProgress } = useScroll();
    const scaleX = useSpring(scrollYProgress, { stiffness: 100, damping: 30, restDelta: 0.001 });

    return (
        <Router>
            <ScrollToTop />
            <AnimatePresence mode="wait">
                {loading ? (
                    <Preloader key="preloader" onComplete={() => setLoading(false)} />
                ) : (
                    <div key="content">
                        <motion.div className="scroll-progress" style={{ scaleX }} />
                        <div style={{ position: "relative", overflowX: "hidden" }}>
                            <GridBackground />
                            <Navbar />
                             <Routes>
                                <Route path="/" element={<LandingPage />} />
                            </Routes>
                            <Footer />
                        </div>
                    </div>
                )}
            </AnimatePresence>
        </Router>
    );
}

// Scroll restoration helper
function ScrollToTop() {
    const { pathname } = useLocation();
    useEffect(() => {
        window.scrollTo(0, 0);
    }, [pathname]);
    return null;
}